import { HONEYPOT_FIELD } from '../../../shared/schemas/fields';
import { formsContent } from '../../content/forms';

/**
 * The hidden anti-spam field carried by every public form (spec section 19,
 * CLAUDE.md "Honeypot from day one").
 *
 * Rendered exactly as `shared/schemas/fields.ts` documents it:
 *
 * - **Visually hidden, not `display: none`.** The clip technique keeps the
 *   input in the layout and in the accessibility tree's reach, because a field
 *   removed with `display: none` is the one thing an unsophisticated form bot
 *   does check for and skip.
 * - **`aria-hidden` container.** A screen reader never reaches it, so nobody
 *   using one can fill it in by mistake and have their signup silently
 *   discarded.
 * - **`tabIndex={-1}`.** Tab order goes straight past it for a sighted keyboard
 *   user.
 * - **`autoComplete="off"`.** A password manager or browser autofill must not
 *   helpfully type a URL into it and trip the trap for a real person.
 *
 * The field is named `website` because that is what a bot expects to find. The
 * provider form's *real* website field is `websiteUrl`, so the two never
 * collide in the payload.
 */
export interface HoneypotFieldProps {
  /** Unique per form instance; a page may render more than one form. */
  id: string;
  value: string;
  onChange: (value: string) => void;
}

export function HoneypotField({ id, value, onChange }: HoneypotFieldProps) {
  return (
    <div aria-hidden="true" className="sr-only">
      <label htmlFor={id}>{formsContent.common.honeypot.label}</label>
      <input
        id={id}
        name={HONEYPOT_FIELD}
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        tabIndex={-1}
        autoComplete="off"
        // Belt and braces for the autofill heuristics that ignore
        // autocomplete="off" on a text input.
        data-lpignore="true"
        data-1p-ignore="true"
        // Sized to a pixel so the input stays inside its clipping wrapper. A
        // default-width input would render its box past the right edge of a
        // 320px viewport and count as horizontal overflow (spec section 22),
        // even though the wrapper hides it.
        className="h-px w-px p-0"
      />
    </div>
  );
}
